import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const Payslip = () => {
  const navigate = useNavigate();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  useEffect(() => {
    fetchPayslips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage]);

  const fetchPayslips = async () => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const payload = {
        action: 'get-payslips',
        currentUserId: currentUserId,
        page: pagination.currentPage,
        limit: pagination.limit
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        // Filter to show only Approved payslips, exclude Pending
        const approvedPayslips = (result.data || []).filter(payslip => 
          payslip.status && payslip.status.toLowerCase() === 'approved'
        );
        
        setPayslips(approvedPayslips);
        setPagination(prev => ({
          ...prev,
          currentPage: result.currentPage || prev.currentPage,
          totalPages: result.totalPages || prev.totalPages,
          totalCount: approvedPayslips.length,
          hasNextPage: (result.currentPage || prev.currentPage) < (result.totalPages || prev.totalPages),
          hasPreviousPage: (result.currentPage || prev.currentPage) > 1
        }));
      } else {
        toast.error(result?.message || 'Failed to fetch payslips');
      }
    } catch (error) {
      console.error('Error fetching payslips:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchSinglePayslip = async (id) => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return null;
    }

    const currentUserId = localStorage.getItem('id');
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      return null;
    }

    try {
      const payload = {
        action: 'get-payslip',
        currentUserId: currentUserId,
        id
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;
      if (result?.success && result?.data) {
        return result.data;
      }
      toast.error(result?.message || 'Failed to fetch payslip');
      return null;
    } catch (error) {
      console.error('Error fetching payslip:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
      return null;
    }
  };

  const formatCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '0.00';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '0.00';
    return numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
      'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
    ];
    return months[monthNumber - 1] || '';
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleView = (id) => {
    navigate(`/payslip-view/${id}`);
  };

  const openPrintablePayslip = (data) => {
    if (!data) return;
    const formatDateForPrint = (dateString) => {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch {
        return dateString;
      }
    };
    
    const payslipContent = `
      <style>
        @page {
          size: A4;
          margin: 15mm;
          background: white;
        }
        body {
          background: white;
          margin: 0;
          padding: 0;
        }
        @media print {
          body * {
            visibility: hidden;
          }
          #payslip-print-content, #payslip-print-content * {
            visibility: visible;
          }
          #payslip-print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 0;
            background: white;
          }
          #payslip-print-content th {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background-color: #e0f2f1 !important;
            color: #004d40 !important;
          }
          #payslip-print-content .payslip-title-bar {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background-color: #009688 !important;
            color: #ffffff !important;
          }
          #payslip-print-content .net-row {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          #payslip-print-content .net-row td {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background-color: #f0f0f0 !important;
          }
        }
        @media screen {
          #payslip-print-content {
            display: none;
          }
        }
        #payslip-print-content {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 0;
          color: #333;
          font-size: 14px;
          width: 100%;
          max-width: 210mm;
          box-sizing: border-box;
          background: white;
        }
        @media print {
          #payslip-print-content {
            max-width: 100%;
          }
        }
        #payslip-print-content .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: none;
        }
        #payslip-print-content .header-content {
          flex: 1;
        }
        #payslip-print-content .header img {
          max-height: 80px;
          margin-left: auto;
        }
        #payslip-print-content .title {
          margin: 8px 0 4px 0;
          font-size: 20px;
          font-weight: bold;
        }
        #payslip-print-content .subtitle {
          margin: 0;
          font-size: 13px;
          color: #666;
        }
        #payslip-print-content table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 12px;
        }
        #payslip-print-content td, #payslip-print-content th {
          border: 1px solid #e0e0e0;
          padding: 8px 10px;
          font-size: 13px;
        }
        #payslip-print-content th {
          background-color: #e0f2f1;
          color: #004d40;
          font-weight: bold;
          text-align: left;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        #payslip-print-content .net-row td {
          background: #f0f0f0;
          font-size: 14px;
        }
        #payslip-print-content .footer {
          text-align: center;
          font-size: 12px;
          color: #666;
          font-style: italic;
          margin-top: 12px;
        }
      </style>
      <div id="payslip-print-content">
        <div class="header">
          <div class="header-content">
            <div class="title">InstaTenders Pvt Limited</div>
            <div class="subtitle"> 4/159, Labour Colony 1st Street, 3rd Phase, SIDCO Industrial Estate, Guindy, Chennai- 600032.</div>
          </div>
          <img src="/assets/img/color icon_circle.png" alt="InstaTenders Logo" />
        </div>
        <div class="payslip-title-bar" style="text-align:center; margin-bottom: 18px; font-size: 16px; font-weight: bold; background-color: #009688; color: #ffffff; padding: 12px 20px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          Payslip For ${getMonthName(data.month)} ${data.year}
        </div>
        <table style="width:100%; border-collapse:collapse;">
          <colgroup>
            <col style="width:20%" />
            <col style="width:30%" />
            <col style="width:20%" />
            <col style="width:30%" />
          </colgroup>
          <tbody>
            <tr>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>Employee ID:</strong></td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;">${data.employeeId || '-'}</td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>Name:</strong></td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;">${data.employeeName || '-'}</td>
            </tr>
            <tr>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>Bank:</strong></td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;">${data.bankName || '-'}</td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>Bank A/c No.:</strong></td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;">${data.bankAccountNumber || '-'}</td>
            </tr>
            <tr>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>DOJ:</strong></td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;">${formatDateForPrint(data.dateOfJoining)}</td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>LOP Days:</strong></td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;">${data.lopDays || 0}</td>
            </tr>
            <tr>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>PF No.:</strong></td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;">${data.pfNumber || '-'}</td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>STD Days:</strong></td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;">${data.stdDays || 0}</td>
            </tr>
            <tr>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>Location:</strong></td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;">${data.location || 'Chennai'}</td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>Worked Days:</strong></td>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;">${data.workedDays || 0}</td>
            </tr>
            <tr>
              <td style="padding:8px 12px; border:1px solid #e0e0e0;"><strong>Department:</strong></td>
              <td colspan="3" style="padding:8px 12px; border:1px solid #e0e0e0;">${data.department || '-'}</td>
            </tr>
          </tbody>
        </table>
        <table style="width:100%; border-collapse:collapse; margin-bottom:18px;">
          <colgroup>
            <col style="width:38%" />
            <col style="width:12%" />
            <col style="width:28%" />
            <col style="width:22%" />
          </colgroup>
          <thead>
            <tr>
              <th style="padding:12px; border:1px solid #e0e0e0; background-color:#e0f2f1; color:#004d40; font-weight:bold; text-align:left;">Earnings</th>
              <th style="padding:12px; border:1px solid #e0e0e0; background-color:#e0f2f1; color:#004d40; font-weight:bold; text-align:right; white-space:nowrap;">Amount in Rs.</th>
              <th style="padding:12px; border:1px solid #e0e0e0; background-color:#e0f2f1; color:#004d40; font-weight:bold; text-align:left;">Deductions</th>
              <th style="padding:12px; border:1px solid #e0e0e0; background-color:#e0f2f1; color:#004d40; font-weight:bold; text-align:right; white-space:nowrap;">Amount in Rs.</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;">BASIC</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; text-align:right;">${formatCurrency(data.basic)}</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; border-left:1px solid #e0e0e0;">PROVIDENT FUND</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; text-align:right;">${formatCurrency(data.pf)}</td>
            </tr>
            ${(data.da > 0 || data.lopDeduction > 0) ? `
            <tr>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;">${data.da > 0 ? 'DEARNESS ALLOWANCE' : ''}</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; text-align:right;">${data.da > 0 ? formatCurrency(data.da) : ''}</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; border-left:1px solid #e0e0e0;">${data.lopDeduction > 0 ? 'LOP DEDUCTION' : ''}</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; text-align:right;">${data.lopDeduction > 0 ? formatCurrency(data.lopDeduction) : ''}</td>
            </tr>
            ` : ''}
            ${data.hra > 0 ? `
            <tr>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;">HOUSE RENT ALLOWANCE</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; text-align:right;">${formatCurrency(data.hra)}</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; border-left:1px solid #e0e0e0;"></td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;"></td>
            </tr>
            ` : ''}
            ${data.otherAllowance > 0 ? `
            <tr>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;">OTHER ALLOWANCE</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; text-align:right;">${formatCurrency(data.otherAllowance)}</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; border-left:1px solid #e0e0e0;"></td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;"></td>
            </tr>
            ` : ''}
            ${data.miscAllowance > 0 ? `
            <tr>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;">MISCELLANEOUS ALLOWANCE</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; text-align:right;">${formatCurrency(data.miscAllowance)}</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; border-left:1px solid #e0e0e0;"></td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;"></td>
            </tr>
            ` : ''}
            ${data.variablePay > 0 ? `
            <tr>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;">VARIABLE PAY</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; text-align:right;">${formatCurrency(data.variablePay)}</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; border-left:1px solid #e0e0e0;"></td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;"></td>
            </tr>
            ` : ''}
            ${data.variablePayMonthly > 0 ? `
            <tr>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;">VARIABLE PAY-MONTHLY</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; text-align:right;">${formatCurrency(data.variablePayMonthly)}</td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0; border-left:1px solid #e0e0e0;"></td>
              <td style="padding:10px 12px; border:1px solid #e0e0e0;"></td>
            </tr>
            ` : ''}
          </tbody>
          <tfoot>
            <tr>
              <td style="padding:12px; border:1px solid #e0e0e0; font-weight:bold;">GROSS EARNINGS</td>
              <td style="padding:12px; border:1px solid #e0e0e0; text-align:right; font-weight:bold;">${formatCurrency(data.grossEarnings)}</td>
              <td style="padding:12px; border:1px solid #e0e0e0; font-weight:bold;">GROSS DEDUCTIONS</td>
              <td style="padding:12px; border:1px solid #e0e0e0; text-align:right; font-weight:bold;">${formatCurrency((data.grossDeductions || 0) + (data.lopDeduction || 0))}</td>
            </tr>
            <tr class="net-row">
              <td style="padding:12px; border:1px solid #e0e0e0; font-weight:bold; background:#f0f0f0;">NET PAY</td>
              <td style="padding:12px; border:1px solid #e0e0e0; text-align:right; font-weight:bold; background:#f0f0f0;">${formatCurrency(data.netPay)}</td>
              <td style="padding:12px; border:1px solid #e0e0e0; background:#f0f0f0;"></td>
              <td style="padding:12px; border:1px solid #e0e0e0; background:#f0f0f0;"></td>
            </tr>
          </tfoot>
        </table>
        <div class="footer"><strong>This is a computer generated payslip and does not require signature and stamp.</strong></div>
      </div>
    `;
    
    // Generate filename: Payslip-Username Month Year
    const formatUsername = (name) => {
      if (!name) return 'Employee';
      return name
        .split(/\s+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };
    
    const monthName = getMonthName(data.month);
    const username = formatUsername(data.employeeName);
    const filename = `Payslip-${username} ${monthName} ${data.year}`;
    
    // Save original title
    const originalTitle = document.title;
    
    // Set document title for PDF filename
    document.title = filename;
    
    // Remove existing print content if any
    const existingPrintContent = document.getElementById('payslip-print-content');
    if (existingPrintContent) {
      existingPrintContent.remove();
    }
    
    // Create and append the print content
    const printDiv = document.createElement('div');
    printDiv.innerHTML = payslipContent;
    document.body.appendChild(printDiv);
    
    // Trigger print
    window.print();
    
    // Remove the print content after printing and restore title
    window.onafterprint = () => {
      const printContent = document.getElementById('payslip-print-content');
      if (printContent) {
        printContent.remove();
      }
      // Restore original title
      document.title = originalTitle;
    };
    
    // Fallback: remove after a delay in case onafterprint doesn't fire
    setTimeout(() => {
      const printContent = document.getElementById('payslip-print-content');
      if (printContent) {
        printContent.remove();
      }
      // Restore original title
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownload = async (id) => {
    const data = await fetchSinglePayslip(id);
    openPrintablePayslip(data);
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-12">
              <h1 className="employee-title">
                Payslip
                <i className="fas fa-file-invoice-dollar ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View and download your monthly payslips
              </p>
            </div>
          </div>

          {/* Payslip Table Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              <div className="employee-table-container">
                {loading ? (
                  <Loading size={120} />
                ) : (
                  <>
                    <div className="employee-table-wrapper">
                      <table className="employee-table">
                        <thead>
                          <tr>
                            <th>SI No.</th>
                            <th>Month</th>
                            <th>Year</th>
                            <th>Net Pay</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payslips.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="employee-empty">
                                No payslips found
                              </td>
                            </tr>
                          ) : (
                            payslips.map((payslip, index) => (
                              <tr key={payslip.id || index}>
                                <td>{getSerialNumber(index)}</td>
                                <td>{getMonthName(payslip.month)}</td>
                                <td>{payslip.year}</td>
                                <td>{formatCurrency(payslip.netPay)}</td>
                                <td>
                                  <div className="employee-actions">
                                    <button
                                      className="employee-action-btn employee-action-view"
                                      onClick={() => handleView(payslip.id)}
                                      title="View"
                                    >
                                      <i className="fa-solid fa-eye"></i>
                                    </button>
                                    <button
                                      className="employee-action-btn employee-action-edit"
                                      onClick={() => handleDownload(payslip.id)}
                                      title="Download"
                                    >
                                      <i className="fa-solid fa-download"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {payslips.length > 0 && pagination.totalPages > 0 && (
                      <div
                        className="employee-pagination"
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}
                      >
                        <div className="employee-pagination-info">
                          Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of {pagination.totalCount} entries
                        </div>
                        <div className="employee-pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.currentPage === 1}
                          >
                            «
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                          >
                            ‹
                          </button>
                          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            ›
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            »
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Payslip;
